import { DeletePortfolioProps } from "../pages/MyFunds/DeletePortfolio";
import { CompanyLogoProps } from "../components/tables/CompanyLogo";
import { CustomDropdownProps } from "../components/visuals/PageFilter/PageFilter";
import {
  DisplayVotingRecordModalParams,
  EngagementItem,
  Vote,
} from "./stewardshipModels";
import { TagProps } from "../components/visuals/CompassTag";
import { VoteAction } from "./stewardshipModels";
import { AvatarMatrixProps, AvatarProps } from "../components/visuals/Avatar";
import { TitleWithCircleTagProps } from "../components/tables/TitleWithCircleTag";
import { DropdownProps } from "../components/visuals/CompassDropdown";
import { DeleteItemProps } from "../components/tables/DeleteItem";
import { FlagKey, TaskPriority } from "./workflowModels";
import { PathwayItem } from "../components/visuals/PathwayProgressBar";
import { FundManagerBadgeProps } from "../components/visuals/FundManagerBadgeList";

export type DataSourceType = {
  key: string | number;
  [key: string]: CellType;
};

export interface FlaggableVote
  extends VoteAction, CustomDropdownProps, TagProps {
  displayVotingRecordModalParams: DisplayVotingRecordModalParams;
  hasRationale?: boolean;
  rationale?: string;
}

export type CellType =
  | string
  | number
  | boolean
  | string[]
  | DeletePortfolioProps
  | DeleteItemProps
  | CompanyLogoProps
  | CustomDropdownProps
  | TagProps
  | TagProps[]
  | VoteAction
  | FlaggableVote
  | AvatarProps
  | AvatarProps[]
  | TitleWithCircleTagProps
  | DropdownProps
  | PathwayItem
  | CellLink
  | AvatarMatrixProps
  | ExpandableTextCell
  | FundManagerBadgeProps[];

export interface ScreenerDataSourceType extends DataSourceType {
  name: string;
  compass_cid: string;
  currency: string;
  added_on: string;
  image: string;
  revenue: number;
  companyAndLogo: CompanyLogoProps;
  flagsDropdown: CustomDropdownProps;
  flagStatus: FlagKey;
}

export interface EngagementDataSourceType extends EngagementItem {
  flagsDropdown: CustomDropdownProps;
}

export interface VotingDataSourceType extends DataSourceType {
  proposalId: string;
  proposalDescription: string;
  proposalType: string;
  managementRecommendation: TagProps;
  alignmentVote: Vote | "-";
}

export interface MyFundsDataSourceType extends DataSourceType {
  portfolio_index_id: string;
  currency: string;
  fund_aum: number;
  added_on: string;
  is_index: boolean;
  pathway: PathwayItem;
  delete: DeletePortfolioProps;
}

export interface MyFundsDataSourceTypeWithoutKey {
  [key: string]: string | number | boolean | DeletePortfolioProps | PathwayItem;
  portfolio_index_id: string;
  currency: string;
  fund_aum: number;
  added_on: string;
  is_index: boolean;
  pathway: PathwayItem;
  delete: DeletePortfolioProps;
}

export interface Metric {
  fund_aum: number;
  added_on: string;
  metric_id: string;
  value_numeric?: number;
  value_text?: string;
  portfolio_index_id: string;
  Compass_Metric_Display_Name: string;
  currency: string;
  is_index: boolean;
  tCO2e_1_5C_or_1_75C: number;
  tCO2e_2C: number;
  tCO2e_2_7C_or_3C: number;
}

export interface CompanyMetric {
  name: string;
  compass_cid: string;
  metric_id: string;
  value_numeric: number | null;
  value_text: string | null;
  Compass_Metric_Display_Name: string;
  currency: string;
  image: string;
  revenue: number;
  added_on: string;
  portfolio_index_id: string;
  metric_type: string;
  holding_weight: number;
  geo_level_2: string;
  gics_sector_name: string;
  flag_status: FlagKey;
  Is_Numeric: boolean;
}

export interface ColumnType {
  type: TableCellType;
  columnKey: string;
  isComparative?: boolean;
}

export enum TableCellType {
  TEXT = "TEXT",
  FUND_LINK = "FUND_LINK",
  NUMBER = "NUMBER",
  PERCENTAGE = "PERCENTAGE",
  PATHWAY = "PATHWAY",
  NUMBER_W_BULLET_CHART = "NUMBER_W_BULLET_CHART",
  DELETE = "DELETE",
  DELETE_PORTFOLIO = "DELETE_PORTFOLIO",
  LOADING = "LOADING",
  COMPANY_LOGO = "COMPANY_LOGO",
  DROPDOWN = "DROPDOWN",
  FLAGGABLE_VOTE = "FLAGGABLE_VOTE",
  DATE = "DATE",
  TAG = "TAG",
  TAGS = "TAGS",
  AVATAR = "AVATAR",
  AVATAR_STACK = "AVATAR_STACK",
  AVATAR_MATRIX = "AVATAR_MATRIX",
  PRIORITY = "PRIORITY",
  CIRCLE_BADGE_TITLE = "CIRCLE_BADGE_TITLE",
  SELECT = "SELECT",
  LINK = "LINK",
  COUNTRY_W_FLAG = "COUNTRY_W_FLAG",
  VOTING_ALIGNMENT_BAR = "VOTING_ALIGNMENT_BAR",
  TITLE = "TITLE",
  EXPANDABLE_TEXT = "EXPANDABLE_TEXT",
  FUND_MANAGER_BADGES = "FUND_MANAGER_BADGES",
}

export interface StewardshipScreenerDataSource extends DataSourceType {
  company: CompanyLogoProps;
  manager_id: AvatarProps[];
  portfolio_index_id: FundManagerBadgeProps[];
  completed_milestones: number;
  voh: number;
  engagement_count: number;
  corporate_objective_count: number;
}

export interface VotingCompanySummaryDataSource extends DataSourceType {
  company: CompanyLogoProps;
  manager: string;
  aligned_votes_count: number;
  categories: TagProps[];
  misaligned_votes_count: number;
  total_votes_count: TagProps;
  alignedPct: number;
  countryIso2: string;
  totalRecordsCount: number;
}

export interface TaskDataSource extends DataSourceType {
  title: TitleWithCircleTagProps;
  priority: TaskPriority;
  description: string;
  status: TagProps;
  date_created: string; // ISO 8601 date string
  date_completed: string; // ISO 8601 date string or "-"
  date_due: TagProps | "-";
  assigned_to: AvatarProps;
  last_updated: AvatarProps;
  note_count: DropdownProps | "-";
  delete: DeleteItemProps;
}

export interface CellLink {
  link: string;
  displayValue: string;
  newTab?: boolean;
}

export interface ExpandableTextCell {
  text: string;
  maxLines?: number;
}
